import { BetsQueryKeys } from '@/api/bets/queryKey';
import { useUserAccount } from '@/api/user';
import { GameIdNamespace } from '@/enums/gameId';
import { store } from '@/lib/store';
import { addLiveBet } from '@/lib/store/live-bets/actions';
import { DetailedBet } from '@/models/detailed-bet';
import { GamingAggregatorId } from '@/models/gaming-aggregator/id';
import { Nullable } from '@starsoft/common/interfaces';
import { interceptorDataParser } from '@starsoft/common/utils/parsers';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { useSocketIoFactory } from './useSocketIoFactory';
export default function useBetsSocket() {
  const queryClient: QueryClient = useQueryClient();
  const pathname = usePathname();

  const { user } = useUserAccount();
  const socket: Nullable<Socket> = useSocketIoFactory({
    gateway: 'bets',
    isProtected: true,
  });

  const onConnect = useCallback(() => {
    if (!socket || !user?.player?.id) return;

    socket.emit('subscribe', {
      playerId: user?.player?.id,
    });
  }, [socket, user]);

  const onDisconnect = useCallback(() => {
    if (!socket) return;

    socket.emit('unsubscribe', {
      playerId: user?.player?.id,
    });
  }, [socket, user]);

  const onUpdate: (payload: DetailedBet) => void = useCallback(
    (data: DetailedBet) => {
      const gameId = GameIdNamespace.getGameIdByGameSlug(pathname);
      if (gameId) {
        queryClient.setQueryData(
          [BetsQueryKeys.BetsOriginalsMe, { gameId }],
          data,
        );
        return;
      }

      if (
        data?.aggregator?.id?.toString() ===
        GamingAggregatorId.Softswiss.toString()
      ) {
        queryClient.invalidateQueries({ queryKey: [BetsQueryKeys.Me] });
        queryClient.invalidateQueries({ queryKey: [BetsQueryKeys.MeInfinity] });
        return;
      }

      if (
        data?.aggregator?.id?.toString() === GamingAggregatorId.Betby.toString()
      ) {
        //notification - future
        return;
      }
    },
    [queryClient],
  );

  const onPayout: (payload: DetailedBet) => void = useCallback(
    (data: DetailedBet) => {
      store.dispatch(addLiveBet(data));
    },
    [queryClient],
  );

  function setServerOnEvents() {
    if (!socket) return;
    socket.on('connect', onConnect);
    socket.on('live-bets-update', (data: DetailedBet) => {
      const parsedData = interceptorDataParser(data);
      onUpdate(parsedData);
    });
    socket.on('payout', (data: DetailedBet) => {
      const parsedData = interceptorDataParser(data);
      onPayout(parsedData);
    });
    socket.on('disconnect', onDisconnect);
  }
  useEffect(setServerOnEvents, [socket]);
}
